'use strict';

import { DOM } from '../helpers/DOM.js';

export class SignatureField {
	constructor() {
		DOM.observeElements('.levcharity_signature_wrapper', (wrapper) => {
			this.#initField(wrapper);
		});
	}

	#initField(wrapper) {
		const canvas = DOM.el('canvas.levcharity_signature_canvas', wrapper);
		const hiddenInput = DOM.el('input[type="hidden"]', wrapper);
		const clearBtn = DOM.el('.levcharity_signature_clear', wrapper);
		const undoBtn = DOM.el('.levcharity_signature_undo', wrapper);
		const redoBtn = DOM.el('.levcharity_signature_redo', wrapper);
		const errorMessage = DOM.el('.levcharity_error_message', wrapper);
		const uploadBtn = DOM.el('.levcharity_signature_upload', wrapper);

		if (!canvas || !hiddenInput) return;

		const ctx = canvas.getContext('2d');
		let drawing = false;
		let lastPos = null;
		let lastTime = 0;
		let lastLineWidth = 1;
		let saveTimeout = null;

		// --- Undo/Redo ---
		let history = [];
		let historyIndex = -1;
		const maxHistorySize = 20;

		const saveState = (addHistory = true) => {
			if (!addHistory) return;
			const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

			if (historyIndex < history.length - 1) {
				history = history.slice(0, historyIndex + 1);
			}

			history.push(imageData);
			historyIndex++;

			if (history.length > maxHistorySize) {
				history.shift();
				historyIndex--;
			}
		};

		const restoreState = (index) => {
			if (index < 0 || index >= history.length) return;
			const imageData = history[index];
			ctx.putImageData(imageData, 0, 0);
			hiddenInput.value = canvas.toDataURL('image/png');
			DOM.triggerEvent(hiddenInput, 'change');
		};

		const undo = () => {
			if (historyIndex > 0) {
				historyIndex--;
				restoreState(historyIndex);
			}
		};

		const redo = () => {
			if (historyIndex < history.length - 1) {
				historyIndex++;
				restoreState(historyIndex);
			}
		};

		// --- Canvas setup ---
		canvas.width = canvas.offsetWidth;
		canvas.height = canvas.offsetHeight;
		const baselineOffset = 30;

		const drawBaseline = () => {
			ctx.strokeStyle = '#ccc';
			ctx.lineWidth = 1;
			ctx.beginPath();
			ctx.moveTo(0, canvas.height - baselineOffset);
			ctx.lineTo(canvas.width, canvas.height - baselineOffset);
			ctx.stroke();
			ctx.strokeStyle = '#000';
		};
		drawBaseline();

		ctx.lineJoin = 'round';
		ctx.lineCap = 'round';
		ctx.strokeStyle = '#000';

		// Save initial empty state
		saveState();

		const lerp = (start, end, t) => start + (end - start) * t;

		const getPosition = (e) => {
			const rect = canvas.getBoundingClientRect();
			if (e.touches && e.touches[0]) {
				return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
			}
			return { x: e.clientX - rect.left, y: e.clientY - rect.top };
		};

		// --- Save & Validation ---
		const saveSignature = () => {
			if (!this.#validateCanvas(canvas, ctx)) {
				if (errorMessage) errorMessage.textContent = 'Please provide a valid signature!';
				hiddenInput.value = '';
				DOM.triggerEvent(hiddenInput, 'change');
				return;
			}
			hiddenInput.value = canvas.toDataURL('image/png');
			DOM.triggerEvent(hiddenInput, 'change');
			if (errorMessage) errorMessage.textContent = '';
		};

		const scheduleSave = () => {
			clearTimeout(saveTimeout);
			saveTimeout = setTimeout(saveSignature, 500);
		};

		// --- Make canvas focusable for keyboard shortcuts ---
		canvas.tabIndex = 0;
		const focusCanvas = () => canvas.focus();

		// --- Drawing ---
		const startDrawing = (e) => {
			e.preventDefault();
			drawing = true;
			lastPos = getPosition(e);
			lastTime = Date.now();
			lastLineWidth = 1;
			ctx.beginPath();
			ctx.moveTo(lastPos.x, lastPos.y);
			if (errorMessage) errorMessage.textContent = '';
			focusCanvas();
		};

		const draw = (e) => {
			if (!drawing) return;
			e.preventDefault();

			const pos = getPosition(e);
			const now = Date.now();
			const dt = now - lastTime;
			const dx = pos.x - lastPos.x;
			const dy = pos.y - lastPos.y;
			const dist = Math.sqrt(dx * dx + dy * dy);
			const speed = dist / (dt || 1);

			const minLine = 1;
			const maxLine = 3;
			let targetLineWidth = maxLine - Math.min(speed, maxLine - minLine);
			targetLineWidth = Math.max(minLine, Math.min(maxLine, targetLineWidth));
			const smoothFactor = 0.2;
			const lineWidth = lerp(lastLineWidth, targetLineWidth, smoothFactor);
			ctx.lineWidth = lineWidth;
			lastLineWidth = lineWidth;

			const mid = {
				x: (lastPos.x + pos.x) / 2,
				y: (lastPos.y + pos.y) / 2
			};
			ctx.quadraticCurveTo(lastPos.x, lastPos.y, mid.x, mid.y);
			ctx.stroke();

			lastPos = pos;
			lastTime = now;

			scheduleSave();
		};

		const stopDrawing = () => {
			if (!drawing) return;
			drawing = false;
			lastPos = null;
			lastTime = 0;
			saveState(true);
			scheduleSave();
		};

		const clear = () => {
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			drawBaseline();
			hiddenInput.value = '';
			DOM.triggerEvent(hiddenInput, 'change');
			saveState(true);
			if (errorMessage) errorMessage.textContent = '';
		};

		// --- Image Upload ---
		const handleUpload = (file) => {
			const reader = new FileReader();
			reader.onload = (e) => {
				const img = new Image();
				img.onload = () => {
					ctx.clearRect(0, 0, canvas.width, canvas.height);
					drawBaseline();

					const offCanvas = document.createElement('canvas');
					offCanvas.width = canvas.width;
					offCanvas.height = canvas.height;
					const offCtx = offCanvas.getContext('2d');

					const imgAspect = img.width / img.height;
					const canvasAspect = canvas.width / canvas.height;

					let drawWidth, drawHeight, drawX, drawY;

					if (imgAspect > canvasAspect) {
						drawWidth = canvas.width;
						drawHeight = canvas.width / imgAspect;
						drawX = 0;
						drawY = (canvas.height - drawHeight) / 2;
					} else {
						drawWidth = canvas.height * imgAspect;
						drawHeight = canvas.height;
						drawX = (canvas.width - drawWidth) / 2;
						drawY = 0;
					}

					offCtx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
					const imgData = offCtx.getImageData(0, 0, canvas.width, canvas.height);
					const data = imgData.data;

					// Simple thresholding for dark strokes
					for (let i = 0; i < data.length; i += 4) {
						const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
						if (data[i + 3] > 0 && gray < 128) {
							data[i] = 0;
							data[i + 1] = 0;
							data[i + 2] = 0;
							data[i + 3] = 255;
						} else {
							data[i + 3] = 0;
						}
					}

					offCtx.putImageData(imgData, 0, 0);
					ctx.drawImage(offCanvas, 0, 0);

					hiddenInput.value = canvas.toDataURL('image/png');
					DOM.triggerEvent(hiddenInput, 'change');
					saveState(true);
				};
				img.src = e.target.result;
			};
			reader.readAsDataURL(file);
		};

		// --- Events ---
		DOM.addEvent('mousedown,touchstart', canvas, startDrawing);
		DOM.addEvent('mousemove,touchmove', canvas, draw);
		DOM.addEvent('mouseup,touchend', window, stopDrawing);
		DOM.addEvent('mouseleave', canvas, () => {
			lastPos = null;
			lastTime = 0;
		});
		DOM.addEvent('mouseenter', canvas, (e) => {
			if (drawing) {
				lastPos = getPosition(e);
				lastTime = Date.now();
			}
		});

		if (clearBtn instanceof Element) DOM.addEvent('click', clearBtn, clear);
		if (undoBtn instanceof Element) DOM.addEvent('click', undoBtn, () => {
			undo();
			canvas.focus(); // keep keyboard shortcuts working
		});

		if (redoBtn instanceof Element) DOM.addEvent('click', redoBtn, () => {
			redo();
			canvas.focus(); // keep keyboard shortcuts working
		});
		if (uploadBtn instanceof Element) {
			DOM.addEvent('click', uploadBtn, () => {
				const fileInput = document.createElement('input');
				fileInput.type = 'file';
				fileInput.accept = 'image/*';
				fileInput.onchange = () => {
					if (fileInput.files && fileInput.files[0]) handleUpload(fileInput.files[0]);
				};
				fileInput.click();
			});
		}

		// --- Keyboard shortcuts ---
		DOM.addEvent('keydown', document, (e) => {
			if (!wrapper.contains(e.target)) return;

			const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

			// Undo
			if ((!isMac && e.ctrlKey && e.key === 'z' && !e.shiftKey) ||
				(isMac && e.metaKey && e.key === 'z' && !e.shiftKey)) {
				e.preventDefault();
				undo();
			}

			// Redo
			if ((!isMac && e.ctrlKey && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) ||
				(isMac && e.metaKey && e.shiftKey && e.key === 'z')) {
				e.preventDefault();
				redo();
			}
		});
	}

	#validateCanvas(canvas, ctx) {
		const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
		const pixels = imageData.data;
		let blackPixelCount = 0;

		for (let i = 0; i < pixels.length; i += 4) {
			const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2], a = pixels[i + 3];
			if (a > 0 && !(r > 240 && g > 240 && b > 240)) blackPixelCount++;
		}

		const scale = (canvas.width * canvas.height) / (300 * 150);
		const minPixels = 1500 * scale;
		const maxPixels = 6000 * scale;

		return blackPixelCount >= minPixels && blackPixelCount <= maxPixels;
	}
}
